/**
 * FastSimon Custom Discount Badges and Percentage Implementation
 * Features:
 * 1. Display percentage discount next to price on product cards when there is a compare at price
 * 2. Display on-sale badge on product cards when there is a compare at price
 * 3. Display BOGO badge on product cards when product has BOGO tags
 */

(function () {
    'use strict';

    /* Get all product elements to loop on */
    function getProductElements(element) {
        return [...element.querySelectorAll('.fs-results-product-card')];
    }

    /* Remove existing discount percentage label */
    function removeDiscountPercentage(productElement) {
        const discountLabel = productElement.querySelector('.fs-discount-percentage');
        if (discountLabel) {
            discountLabel.remove();
        }
    }

    /* Remove existing on-sale badge */
    function removeOnSaleBadge(productElement) {
        const saleBadge = productElement.querySelector('.fs-on-sale-badge');
        if (saleBadge) {
            saleBadge.remove();
        }
    }

    /* Remove existing BOGO badge */
    function removeBogoBadge(productElement) {
        const bogoBadge = productElement.querySelector('.fs-bogo-badge');
        if (bogoBadge) {
            bogoBadge.remove();
        }
    }

    /* Remove existing out of stock badge */
    function removeOutOfStockBadge(productElement) {
        const outOfStockBadge = productElement.querySelector('.fs-out-of-stock-badge');
        if (outOfStockBadge) {
            outOfStockBadge.remove();
        }
    }

    /* Check if product has BOGO tags */
    function hasBOGOTag(data) {
        if (!data.tags || !Array.isArray(data.tags)) {
            return false;
        }

        return data.tags.some(tag =>
            tag.toLowerCase() === 'bogo' ||
            tag.toLowerCase() === 'buy one get one'
        );
    }

    function isDisabledProduct(data) {
        if (!data.tags || !Array.isArray(data.tags)) {
            return false;
        }
        return data.tags.some(tag =>
            tag.toLowerCase() === 'disabled'
        );
    }

    /* Check if product has hidden tag */
    function isHiddenProduct(data) {
        if (!data.tags || !Array.isArray(data.tags)) {
            return false;
        }
        return data.tags.some(tag =>
            tag.toLowerCase() === 'hidden' || tag.toLowerCase() === 'thca'
        );
    }

    /* Ensure badges wrapper exists, create if needed */
    function ensureBadgesWrapper(productElement) {
        let badgesWrapper = productElement.querySelector('.fs-badges-wrapper');

        // If badges wrapper doesn't exist, create it as first child of image wrapper
        if (!badgesWrapper) {
            const imageWrapper = productElement.querySelector('.fs-serp-image-wrapper');
            if (imageWrapper) {
                badgesWrapper = document.createElement('div');
                badgesWrapper.className = 'fs-badges-wrapper';
                imageWrapper.insertBefore(badgesWrapper, imageWrapper.firstChild);
            }
        }

        return badgesWrapper;
    }

    /* Add discount percentage next to price */
    function addDiscountPercentage(productElement, data) {
        const discountPercent = Math.round(((data.comparePrice - data.price) / data.comparePrice) * 100);
        const priceContainer = productElement.querySelector('.compare-container');

        if (priceContainer && productElement.querySelectorAll('.fs-discount-percentage').length === 0) {
            const discountHTML = `
                <div class="fs-discount-percentage">
                    -${discountPercent}%
                </div>
            `;
            // Insert inside price container instead of after it
            priceContainer.insertAdjacentHTML('beforeend', discountHTML);
        }
    }

    /* Add BOGO badge to product */
    function addBogoBadge(productElement) {
        const badgesWrapper = ensureBadgesWrapper(productElement);
        const emptySaleBadge = productElement.querySelector('.fs-empty-sale-badge');
        const outofstockBadge = productElement.querySelector('.out-of-stock-wrapper');

        if (badgesWrapper && productElement.querySelectorAll('.fs-bogo-badge').length === 0) {
            const bogoBadgeHTML = `
                <div class="fs-bogo-badge">
                    BOGOHO
                </div>
            `;

            // Replace empty sale badge or add to badges wrapper
            if (emptySaleBadge) {
                emptySaleBadge.outerHTML = bogoBadgeHTML;
            } else {
                badgesWrapper.insertAdjacentHTML('afterbegin', bogoBadgeHTML);
            }
        }
    }

    /* Add on-sale badge to product */
    function addOnSaleBadge(productElement) {
        const badgesWrapper = ensureBadgesWrapper(productElement);
        const emptySaleBadge = productElement.querySelector('.fs-empty-sale-badge');
        const outofstockBadge = productElement.querySelector('.out-of-stock-wrapper');

        if (badgesWrapper && productElement.querySelectorAll('.fs-on-sale-badge').length === 0) {
            const saleBadgeHTML = `
                <div class="fs-on-sale-badge">
                    On Sale
                </div>
            `;

            // Replace empty sale badge or add to badges wrapper
            if (emptySaleBadge) {
                emptySaleBadge.outerHTML = saleBadgeHTML;
            } else {
                badgesWrapper.insertAdjacentHTML('beforeend', saleBadgeHTML);
            }
        }
    }

    /* Add out of stock badge to product */
    function addOutOfStockBadge(productElement) {
        const badgesWrapper = ensureBadgesWrapper(productElement);
        const emptySaleBadge = productElement.querySelector('.fs-empty-sale-badge');

        if (badgesWrapper && productElement.querySelectorAll('.fs-out-of-stock-badge').length === 0) {
            const outOfStockBadgeHTML = `
                <div class="out-of-stock-wrapper fs-out-of-stock-badge">
                    Out of stock
                </div>
            `;

            // Replace empty sale badge or add to badges wrapper
            if (emptySaleBadge) {
                emptySaleBadge.outerHTML = outOfStockBadgeHTML;
            } else {
                badgesWrapper.insertAdjacentHTML('afterbegin', outOfStockBadgeHTML);
            }
        }
    }

    /* Main processing function */
    function processProducts(products, element) {
        for (const productElement of getProductElements(element)) {
            try {
                /* Get product data */
                const productID = productElement.dataset.productId;
                const data = products[productID];

                if (!data) {
                    continue;
                }

                /* Check if product has hidden tag and user is not logged in */
                const isHidden = isHiddenProduct(data) || isDisabledProduct(data);
                if (isHidden && (!window.customer || !window.customer.id)) {
                    // Hide the product completely for non-logged-in users
                    productElement.style.display = 'none';
                    continue;
                } else if (isHidden) {
                    // Show the product if user is logged in
                    productElement.style.display = '';
                }

                // const isDisabled = isDisabledProduct(data);

                // if(isDisabled) {
                //     if(!window.customer || !window.customer.id) {
                //         productElement.classList.add('disabled');
                        
                //         // Prevent all interactions with the product element
                //         productElement.style.pointerEvents = 'none';
                //         productElement.style.userSelect = 'none';
                        
                //         // Add click handler for login redirect
                //         const clickHandler = function(e) {
                //             e.preventDefault();
                //             e.stopPropagation();
                //             window.location.href = '/account/login?return_url=' + encodeURIComponent(window.location.pathname);
                //         };
                        
                //         // Re-enable pointer events just for the overlay click
                //         productElement.style.pointerEvents = 'auto';
                //         productElement.addEventListener('click', clickHandler);
                //     }
                // }

                /* Check if product is sold out */
                const isSoldOut = data.isSoldOut === true;

                /* Check if product has compare price and is on sale */
                const hasComparePrice = parseFloat(data.comparePrice) > 0;
                const isOnSale = hasComparePrice && parseFloat(data.comparePrice) > parseFloat(data.price);

                /* Check if product has BOGO tag */
                const isBOGO = hasBOGOTag(data);

                if (isBOGO) {
                    addBogoBadge(productElement);
                } else {
                    removeBogoBadge(productElement);
                }

                if (isOnSale) {
                    addOnSaleBadge(productElement);
                    addDiscountPercentage(productElement, data);
                } else {
                    removeOnSaleBadge(productElement);
                    removeDiscountPercentage(productElement);
                }

                if (isSoldOut) {
                    addOutOfStockBadge(productElement);
                } else {
                    removeOutOfStockBadge(productElement);
                }
            } catch (error) {
                console.error('Error processing product element:', error, productElement);
            }
        }
    }

    /* Register FastSimon hooks */
    function registerHooks() {
        window.SerpOptions.registerHook("serp-product-grid", ({ products, element }) => {
            processProducts(products, element);
        });

        // Also register for product list updates
        window.SerpOptions.registerHook("serp-product-list", ({ products, element }) => {
            processProducts(products, element);
        });
    }

    /* Initialize the functionality */
    function init() {
        if (window.SerpOptions) {
            registerHooks();
        } else {
            window.addEventListener('fast-serp-ready', function () {
                registerHooks();
            });
        }
    }

    /* Start the initialization process */
    document.addEventListener('DOMContentLoaded', function () {
        init();
    });

    // If DOM is already loaded
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            init();
        });
    } else {
        init();
    }
})();